/**
 * RuntimeConfig — envelope returned by GET /v1/runtime-config.
 *
 * Shape contract co-owned with `context/ConfigContext.tsx` (sub-card #B, LLL).
 * Backend (sub-card #A, HHH) produces it; ConfigProvider hydrates it;
 * components read via `useConfig()`; utility modules read via
 * `getRuntimeConfig()` (window snapshot).
 *
 * Sub-card #C (this file) extracts the type so it can be imported anywhere
 * without pulling in React.
 *
 * Migration mapping (from VITE_*):
 *   VITE_API_URL / VITE_API_BASE_URL / VITE_FLEET_API_URL /
 *     VITE_FLEET_HTTP_URL / VITE_ADMIN_API_BASE / VITE_ADMIN_FLEET_API_BASE
 *       → apiBaseUrl
 *   VITE_COGNITO_*    → cognito.*
 *   VITE_STRIPE_*     → stripe.publishableKey
 *   VITE_SENTRY_DSN   → sentry.dsn
 *   VITE_UMAMI_*      → umami.* (self-hosted analytics.2hives.ai — replaces PostHog 2026-06-02)
 *   VITE_BUILD_SHA    → buildSha
 *   VITE_STAGE / VITE_ENV → stage
 *   VITE_USE_MOCK_PLATFORM_API → featureFlags.useMockPlatformApi
 */

export interface CognitoEnvelope {
  poolId:   string;
  clientId: string;
  region:   string;
  /** Hosted UI domain — e.g. "https://2hives-admin.auth.ap-southeast-2.amazoncognito.com" */
  domain:   string;
}

export interface StripeEnvelope {
  publishableKey: string;
}

export interface SentryEnvelope {
  dsn: string;
  /**
   * Card #3095 — optional override for the Sentry/GlitchTip environment tag.
   * When unset, the SPA falls back to `import.meta.env.MODE`. Useful to
   * label staging branches without rebuilding (operator sets the SSM
   * param `/2hives/runtime-config/sentry/environment`).
   */
  environment?: string;
}

/**
 * Self-hosted Umami analytics — replaces PostHog 2026-06-02.
 * `websiteId` is the Umami dashboard's `data-website-id` UUID.
 * `host` is the public origin of the Umami deployment (no trailing slash).
 */
export interface UmamiEnvelope {
  websiteId: string;
  host:      string;
}

/** Full runtime-config envelope returned by `/v1/runtime-config`. */
export interface RuntimeConfig {
  apiBaseUrl:    string;
  wsBaseUrl:     string;
  cognito:       CognitoEnvelope;
  stripe:        StripeEnvelope;
  sentry:        SentryEnvelope;
  umami:         UmamiEnvelope;
  featureFlags:  Record<string, boolean>;
  buildSha:      string;
  stage:         string;
  configVersion: string;
}

/**
 * Window-attached config snapshot — read path for non-hook contexts.
 *
 * React components use `useConfig()`. Utility modules (`api/*.ts`, `lib/*.ts`)
 * cannot call hooks, so `ConfigProvider` mirrors the resolved envelope onto
 * `window.__2HIVES_CONFIG__` after hydration. Modules call
 * `getRuntimeConfig()` to read the latest snapshot.
 *
 * Pre-hydration calls return `null` — callers should provide their own
 * bootstrap default (typically the same default ConfigContext uses).
 * Hot-reload (sub-card #E) updates the same global.
 */
declare global {
  interface Window {
    __2HIVES_CONFIG__?: RuntimeConfig;
  }
}

export function getRuntimeConfig(): RuntimeConfig | null {
  if (typeof window !== "undefined" && window.__2HIVES_CONFIG__) {
    return window.__2HIVES_CONFIG__;
  }
  return null;
}

export function setRuntimeConfig(cfg: RuntimeConfig): void {
  if (typeof window !== "undefined") {
    window.__2HIVES_CONFIG__ = cfg;
  }
}

/**
 * Convenience for the most-common utility-module read.
 *
 * Returns the live `apiBaseUrl` from the window snapshot, or the supplied
 * fallback if the snapshot is not yet hydrated. Use in `api/*.ts` /
 * `lib/*.ts` modules that need the base URL at top-level (not inside a hook).
 */
export function getApiBaseUrl(fallback: string): string {
  return getRuntimeConfig()?.apiBaseUrl ?? fallback;
}
